import Phaser from 'phaser';
import { GAME_HEIGHT, SCENE } from '../../config.js';
import { social } from '../../api/social.js';
import { realtime } from '../../api/realtime.js';
import { gameState } from '../../state/gameState.js';
import { visitState } from '../../state/visitState.js';
import { NatalkaSprite } from '../../ui/NatalkaSprite.js';
import { centreForFeet, feetForCentre, fitCharacter } from '../../ui/outdoorScale.mjs';
import { WalkController } from '../../ui/WalkController.js';
import { SceneTransition } from '../../ui/SceneTransition.js';
import { attachRemotePresence } from '../../ui/remotePresence.js';
import { attachRoomChat } from '../../ui/ChatInput.js';
import { renderBackground, renderImageWhenReady, renderPlacements, swapTexture } from '../../render/world.js';
import { attachSideScroll } from '../../ui/sideScrollCamera.js';
import { depthSortScene } from '../../ui/depthSort.js';
import { AnimalsController } from '../../ui/AnimalsController.js';
import { showToast } from '../../ui/Toast.js';
import { applyForeground } from '../sceneForeground.js';
import { attachYardLook } from '../yardLook.js';
import { currentPhase } from '../../state/dayCycle.js';
import { ySortDepth } from '../../ui/ySort.js';
import { ensureLoaded, loadCtx, prioritiseFor } from '../../ui/sceneLoading.js';
import { needsFor } from '../../assetManifest.mjs';
import { iconText } from '../../ui/iconText.js';
import { attachVisitHud } from '../../ui/visitHud.js';
import { releaseBikeArt } from '../../ui/bikeArt.js';
import { ownBikeArtKeep } from '../../ui/bikeArtKeys.mjs';
import { bikeSession } from '../../ui/bikeSession.js';
import { FurnitureSeats, placementPairs } from '../../ui/FurnitureSeats.js';
import { HOUSE_ROOMS } from '../../ui/placementPerspective.mjs';

// Load the host's read-only view on first entry; reuse it across room-to-room hops
// (those pass no hostId). Returns the view, or null after redirecting home on failure.
//
// Also clears last run's world refs — and it must stay the FIRST statement of every
// visit scene's create(). Scene instances are reused and create() is async, so Phaser
// ticks update() before the awaited part rebuilds the world; a leftover `scene.animals`
// from the previous visit would resurrect its (already destroyed) sprites during those
// ticks, and the new controller then adds a second full set — 3 animals became 6.
export async function loadVisitView(scene, data) {
  scene.natalka = null;
  scene.walker = null;
  scene.animals = null;
  scene.transition = null;
  scene.stairs = null;
  scene._presence = null;
  scene.furnitureSeats = null;
  scene.bg = null;
  scene._yard = null;
  scene._scroll = null;
  if (data?.hostId && data.hostId !== visitState.hostId) {
    visitState.hostId = data.hostId;
    visitState.hostName = data.hostName ?? '';
    try {
      visitState.view = await social.visit(data.hostId);
    } catch (e) {
      showToast(scene, 'Nie można odwiedzić');
      scene.scene.start(SCENE.OUTSIDE);
      return null;
    }
  }
  if (!visitState.view) { scene.scene.start(SCENE.OUTSIDE); return null; }
  return visitState.view;
}

// Render the read-only world + your own avatar + presence/chat + the visit header.
// Sets scene.natalka and scene.walker. Used by every visitor scene so they're identical.
// `yard: { side, level }` (the front yard and the Podwórko): the host's yard under the live sky, fading into the next
// time of day (scenes/yardLook.js) instead of a static `bgKey`; the scene's update() calls scene._yard.update().
export function setupVisitWorld(scene, view, { roomScene, bgKey, yard, areaKey, spawn, floorPolyline, floorY, blocked, xBounds, spawnX, spawnY, presenceOpts, worldWidth }) {
  if (yard) {
    scene._yard = attachYardLook(scene, { side: yard.side, phase: currentPhase(), level: () => yard.level,
      prioritise: () => prioritiseFor(scene.scene.key, { hostState: view }) });   // the host's world, not mine
  } else {
    scene.bg = worldWidth
      ? renderImageWhenReady(scene, bgKey, worldWidth / 2, GAME_HEIGHT / 2, worldWidth, GAME_HEIGHT)
      : renderBackground(scene, bgKey);
  }
  if (areaKey) {
    scene._placementSprites = renderPlacements(scene, view.placements, areaKey, { perspective: HOUSE_ROOMS.has(areaKey) ? scene.perspective : null });
  }

  // Read-only visit: no 👗 clothing-tweak HUD (you can't edit your outfit here, and its
  // icon otherwise overlaps scene buttons like a room's "← Dom").
  // Spawns are FEET positions ({x, feetY}) on the outdoor scenes; a restored spawnY is a centre.
  const y0 = spawnY ?? (spawn.feetY != null ? centreForFeet(scene, spawn.feetY) : spawn.y);
  // `local: true` — it is still YOUR avatar, just without the wardrobe button: it walks with the
  // animated sheets and remembers its facing from room to room like it does at home.
  scene.natalka = new NatalkaSprite(scene, spawnX ?? spawn.x, y0, { noTweakHud: true, local: true, worldScale: scene.worldScale ?? 1 });
  scene.natalka.setOutfit(window.__myEquipped ?? null);
  scene.natalka.setDepth(10);   // y-sorted every frame in update()
  scene.walker = new WalkController(scene, scene.natalka, {
    onBroadcast: (p) => realtime.move(p),
    mode: 'walk',
    ...(floorPolyline ? { floorPolyline } : {}),
    ...(floorY !== undefined ? { floorY } : {}),
    ...(blocked ? { blocked } : {}),
    ...(xBounds ? { xBounds } : {}),
  });
  fitCharacter(scene);

  const room = `house:${visitState.hostId}:${roomScene}`;
  scene._presence = attachRemotePresence(scene, room, { ...(presenceOpts || {}) });
  attachRoomChat(scene, room);
  // The host's sofas and beds: anyone in the room can sit on them (tap a free spot, or walk up to one).
  if (areaKey) {
    scene.furnitureSeats = new FurnitureSeats(scene, {
      natalka: scene.natalka, walker: scene.walker, tapZones: true,
      getPlacements: () => placementPairs(scene._placementSprites),
      getPeers: () => scene._presence?.avatars.values() ?? [],
    });
  }
  // The host's parked bikes and any peer's ride load their atlases here; leaving keeps only the player's own.
  scene.events.once('shutdown', () => {
    const rig = scene.cache.json.get('bike_rig');
    if (rig) releaseBikeArt(scene, ownBikeArtKeep({ rig, bikes: gameState.raw?.bikes, char: window.__myCharacter ?? 'natalka',
      equipped: gameState.raw?.inventory?.equipped, riding: bikeSession.riding, ridingId: bikeSession.ridingId, look: window.__myLook ?? null }));
  });

  if (areaKey) {
    const animals = new AnimalsController(scene, {
      sceneId: areaKey,
      ownerId: visitState.hostId,
      getPlayers: () => {
        const players = [];
        if (window.__myId) players.push({ id: window.__myId, x: scene.natalka.x, y: scene.natalka.y });
        if (scene._presence) for (const [id, av] of scene._presence.avatars) players.push({ id, x: av.sprite.x, y: av.sprite.y });
        return players;
      },
      getWorld: () => ({ animals: view.animals, placements: view.placements, catalog: gameState.raw?.catalog }),
    });
    scene.animals = animals;
    // `once` + captured instance: shutdown listeners survive scene restarts, so `.on`
    // with the shared scene.animals slot would pile up and target the wrong controller.
    scene.events.once('shutdown', () => animals.destroy());
  }

  attachVisitHud(scene, `{house} ${visitState.hostName}`);
}

// Y-sort the visitor's own avatar and every peer avatar against the scene's foreground
// occluders, mirroring BackyardScene._depthSort. Without this the
// visitor sat at a fixed depth 10 (peers at 9) and applyForeground's layers — depth
// ySortDepth(baseY), i.e. 428–450 for the altana bench — sliced anyone standing in FRONT
// of the bench. The flat depth-5 placements/crops/host bikes (depth 1) stay behind exactly
// as before: ySortDepth(feetY) is the feet's screen-y, always far above 5 for any position
// the walker can reach, so this is a pure gain over the old fixed 10.
export function depthSortVisitAvatars(scene) {
  // Shared helper: visitor, the host's placed objects and remote peers all y-sort together,
  // so a visitor can stand behind the host's couch instead of always covering it.
  const y = scene.natalka.y;
  depthSortScene(scene, { actor: scene.natalka, placements: scene._placementSprites, presence: scene._presence,
    actorHalfH: feetForCentre(scene, y) - y });
}

// Factory for the simple visitor scenes (everything except Home, which has door zones).
// cfg: { key, roomScene, areaKey, bgKey(view, scene, phase) | yard: { side, level(view) }, spawn, floorPolyline?, exits?, backButton?, navButtons?, decorate? }
//   yard: the host's front yard / Podwórko under the live sky (scenes/yardLook.js), which follows the phase and draws
//   its own foreground (cfg.foregroundKey is then unused).
//   floorPolyline can be a static array (as elsewhere) or a function(view) → array, for
//   scenes whose floor depends on the host's own state (e.g. the backyard's upgrade level).
//   bgKey is re-evaluated every second with the live phase; the bg (and foreground) texture is swapped when it changes.
//   exits      : edge-walk transitions (same as the matching owner scene)
//   backButton : { label, toScene } — a clickable button (e.g. a room's "← Dom")
//   navButtons : [{ label, toScene, x, y }] — extra clickable nav buttons (e.g. → animal yard)
//   decorate   : (scene, view) — extra read-only world drawing on top of the host's world
export function makeVisitScene(cfg) {
  return class extends Phaser.Scene {
    constructor() { super(cfg.key); }

    async create(data) {
      const view = await loadVisitView(this, data);
      if (!view) return;

      // The host's level/placements decide the art: load what this scene lacks for THEIR
      // world before drawing it (staged loading, spec § wizyty).
      // Any visit scene NOT built by makeVisitScene (VisitMainScene, VisitHomeScene) must
      // repeat these two lines itself, plus an `if (!this.sys.isActive()) return;` after the await.
      await ensureLoaded(this, needsFor(this.scene.key, loadCtx({ hostState: view })));
      prioritiseFor(this.scene.key, { hostState: view });

      const bgKeyFor = (phase) => cfg.bgKey?.(view, this, phase);
      // Outdoor visit scenes draw people at real-world scale, like their owner scenes.
      if (cfg.worldScale || cfg.perspective) { this.worldScale = cfg.worldScale ?? 1; this.perspective = cfg.perspective; }

      setupVisitWorld(this, view, {
        roomScene: cfg.roomScene,
        bgKey: bgKeyFor(currentPhase()),
        yard: cfg.yard ? { side: cfg.yard.side, level: cfg.yard.level(view) } : undefined,
        areaKey: cfg.areaKey,
        spawn: cfg.spawn,
        floorPolyline: typeof cfg.floorPolyline === 'function' ? cfg.floorPolyline(view) : cfg.floorPolyline,
        floorY: cfg.floorY,
        // blocked is a factory(view, scene) → (feetX, feetY) ⇒ bool, so collision matches
        // the host's actual level/placements (garden beds, animal-yard huts).
        blocked: cfg.blocked ? cfg.blocked(view, this) : undefined,
        xBounds: cfg.xBounds,
        spawnX: data?.spawnX,
        spawnY: data?.spawnY ?? (data?.spawnFeetY != null ? centreForFeet(this, data.spawnFeetY) : undefined),
        presenceOpts: cfg.presenceOpts ? cfg.presenceOpts(view, this) : undefined,
        worldWidth: cfg.worldWidth,
      });

      if (cfg.decorate) cfg.decorate(this, view);

      // Foreground occlusion follows the same live phase as the background. cfg.foregroundKey
      // lets a scene override which polygon set to use (e.g. the backyard's per-level altana
      // bench) instead of the default cfg.areaKey lookup (areaKey='backyard' has none).
      // (a yard draws its own foreground and follows the phase itself: scenes/yardLook.js)
      const fgKey = cfg.yard ? null : (cfg.foregroundKey ? cfg.foregroundKey(view) : cfg.areaKey);
      const wide = cfg.worldWidth ? { x: cfg.worldWidth / 2, y: GAME_HEIGHT / 2, w: cfg.worldWidth, h: GAME_HEIGHT } : {};
      const fg = fgKey ? applyForeground(this, fgKey, bgKeyFor(currentPhase()), wide) : null;

      // Time-of-day: the cycle anchor is global, so the visitor's own clock gives the
      // host's phase. 1s tick mirrors the owner scenes' _localTick.
      const bg = this.bg;
      const phaseTick = cfg.yard ? null : this.time.addEvent({
        delay: 1000, loop: true,
        callback: () => {
          if (!bg || !bg.scene) return;   // scene torn down mid-flight (async create) → object destroyed
          const key = bgKeyFor(currentPhase());
          if (bg && bg.texture.key !== key && this.textures.exists(key)) {
            swapTexture(bg, key);
            fg?.setTexture(key);
          }
        },
      });
      // `once` + captured instances (see AnimalsController note above): survives restarts.
      this.events.once('shutdown', () => { phaseTick?.remove(); fg?.destroy(); });

      // Runs after the world + foreground exist, so it can wire up features that need both
      // (e.g. the backyard's altana seating, which reads scene.natalka/walker and needs the
      // foreground's bench occlusion already applied).
      if (cfg.after) cfg.after(this, view, { bgKeyFor });
      // A world wider than the screen (the wide Podwórko) scrolls sideways with you.
      if (cfg.worldWidth) this._scroll = attachSideScroll(this, { worldW: cfg.worldWidth, getTarget: () => this.natalka });

      if (cfg.backButton) {
        // Left side, vertically centered — the same place every owner scene puts it.
        const bx = 70, by = GAME_HEIGHT / 2;
        this.add.rectangle(bx, by, 110, 60, 0x000000, 0.7)
          .setScrollFactor(0).setDepth(50).setStrokeStyle(2, 0xffd166)
          .setInteractive({ useHandCursor: true })
          .on('pointerup', () => this.scene.start(cfg.backButton.toScene));
        iconText(this, bx, by, cfg.backButton.label, {
          fontFamily: 'system-ui', fontSize: '22px', color: '#fff', fontStyle: 'bold',
        }).setOrigin(0.5).setScrollFactor(0).setDepth(51);
      }

      for (const nav of (cfg.navButtons || [])) {
        const r = this.add.rectangle(nav.x, nav.y, 150, 56, 0x000000, 0.7)
          .setScrollFactor(0).setDepth(50).setStrokeStyle(2, 0xffd166)
          .setInteractive({ useHandCursor: true })
          .on('pointerup', () => this.scene.start(nav.toScene));
        iconText(this, nav.x, nav.y, nav.label, {
          fontFamily: 'system-ui', fontSize: '18px', color: '#fff', fontStyle: 'bold',
        }).setOrigin(0.5).setScrollFactor(0).setDepth(51);
        void r;
      }

      this.transition = new SceneTransition(this);
      for (const ex of (cfg.exits || [])) this.transition.addExitZone(ex);
    }

    update(_t, dMs) {
      // create() is async — Phaser can tick update() before it finishes (or after a scene
      // restart clears the refs mid-flight). Same guard as VisitMainScene.
      if (!this.natalka) return;
      this._yard?.update(currentPhase(), dMs);
      cfg.onUpdate?.(this, dMs);
      this.walker?.update(dMs);
      if (!this.altanaSeats?.isSeated) fitCharacter(this);
      this.animals?.update(dMs);
      depthSortVisitAvatars(this);
      this._scroll?.update(dMs);
      if (!this.altanaSeats?.isSeated && !this.furnitureSeats?.isSeated) this.transition?.checkExits(this.natalka);
    }
  };
}
